import type { PageParam, CustomUseQueryOptions, CustomUseInfiniteQueryOptions } from '../types';
import { getNextPageParam, getPreviousPageParam } from '../utils';
import { canSkipSseConnect, throttleSseInvalidation } from './sse-invalidation';
import type { SSEApi } from '@/libs/sdk/api/sse';
import type { SSEEvent, SSEMarketBody } from '@/libs/sdk/api/sse/type';
import { useQuery, useInfiniteQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';

type SSESubscription = {
  event: SSEEvent;
  topics?: string[];
};

// --- useSSEQuery ---

type UseSSEQueryConfig<TData> = CustomUseQueryOptions<TData> & {
  queryKey: QueryKey;
  queryFn: () => Promise<TData>;
};

/**
 * Wraps useQuery with SSE-driven cache invalidation.
 * Uses invalidateQueries throttled per query key (sse-invalidation.ts): the
 * leading refetch waits debounceMs, then at most one refetch per 10 s.
 */
export const useSSEQuery = <TData>(
  sse: SSEApi,
  queryConfig: UseSSEQueryConfig<TData>,
  subscriptions: SSESubscription[],
  debounceMs = 1000
) => {
  const queryClient = useQueryClient();
  const { queryKey, queryFn, ...rest } = queryConfig;
  const query = useQuery({ ...rest, queryKey, queryFn });

  // Stabilize queryKey and subscriptions to prevent effect re-runs on every render.
  // queryKey/subscriptions are new array references each render but same content.
  // Each ref keeps the serialized form captured at its last update, so change
  // detection is a plain string compare (2 stringifies per render, not 4).
  const queryKeyStr = JSON.stringify(queryKey);
  const subsStr = JSON.stringify(subscriptions);
  const queryKeyRef = useRef({ value: queryKey, str: queryKeyStr });
  const subsRef = useRef({ value: subscriptions, str: subsStr });
  // eslint-disable-next-line react-hooks/refs -- content-stable key, written during render on purpose
  if (queryKeyRef.current.str !== queryKeyStr) queryKeyRef.current = { value: queryKey, str: queryKeyStr };
  // eslint-disable-next-line react-hooks/refs -- content-stable subscriptions, as above
  if (subsRef.current.str !== subsStr) subsRef.current = { value: subscriptions, str: subsStr };

  const invalidate = useCallback(() => {
    throttleSseInvalidation(queryClient, queryKeyRef.current.value, debounceMs);
  }, [queryClient, queryKeyStr, debounceMs]);

  useEffect(() => {
    if (rest.enabled === false) return;

    const subs = subsRef.current.value;
    for (const sub of subs) {
      const json: SSEMarketBody | undefined = sub.topics ? { topics: sub.topics } : undefined;
      (sse.addEventListener as any)(sub.event, invalidate, json);
    }
    // On SSE reconnect, invalidate to fill the gap. The first connect follows
    // the mount fetch, so fresh data needs no refetch.
    const onConnected = (data?: { connection_id?: string }) => {
      if (!canSkipSseConnect(queryClient, sse, queryKeyRef.current.value, data?.connection_id)) invalidate();
    };
    (sse.addEventListener as any)('base:connected', onConnected);

    return () => {
      for (const sub of subs) {
        const json: SSEMarketBody | undefined = sub.topics ? { topics: sub.topics } : undefined;
        (sse.removeEventListener as any)(sub.event, invalidate, json);
      }
      (sse.removeEventListener as any)('base:connected', onConnected);
    };
  }, [rest.enabled, sse, invalidate, subsStr]);

  return query;
};

// --- useSSEInfiniteQuery ---

type UseSSEInfiniteQueryConfig<TData> = CustomUseInfiniteQueryOptions<TData> & {
  queryKey: QueryKey;
  queryFn: (context: { signal?: AbortSignal; pageParam: PageParam }) => Promise<TData & PageParam>;
  initialPageParam: PageParam;
};

/**
 * Wraps useInfiniteQuery with SSE-driven cache invalidation.
 */
export const useSSEInfiniteQuery = <TData>(
  sse: SSEApi,
  queryConfig: UseSSEInfiniteQueryConfig<TData>,
  subscriptions: SSESubscription[],
  debounceMs = 1000
) => {
  const queryClient = useQueryClient();
  const { queryKey, queryFn, initialPageParam, ...rest } = queryConfig;
  const query = useInfiniteQuery({
    ...rest,
    queryKey,
    queryFn: ({ signal, pageParam }) => queryFn({ signal, pageParam: pageParam as PageParam }),
    initialPageParam,
    getPreviousPageParam,
    getNextPageParam: getNextPageParam as any,
  });

  // Stabilize deps (same pattern as useSSEQuery)
  const queryKeyStr = JSON.stringify(queryKey);
  const subsStr = JSON.stringify(subscriptions);
  const queryKeyRef = useRef({ value: queryKey, str: queryKeyStr });
  const subsRef = useRef({ value: subscriptions, str: subsStr });
  // eslint-disable-next-line react-hooks/refs -- content-stable key, written during render on purpose
  if (queryKeyRef.current.str !== queryKeyStr) queryKeyRef.current = { value: queryKey, str: queryKeyStr };
  // eslint-disable-next-line react-hooks/refs -- content-stable subscriptions, as above
  if (subsRef.current.str !== subsStr) subsRef.current = { value: subscriptions, str: subsStr };

  const invalidate = useCallback(() => {
    throttleSseInvalidation(queryClient, queryKeyRef.current.value, debounceMs);
  }, [queryClient, queryKeyStr, debounceMs]);

  useEffect(() => {
    if (rest.enabled === false) return;

    const subs = subsRef.current.value;
    for (const sub of subs) {
      const json: SSEMarketBody | undefined = sub.topics ? { topics: sub.topics } : undefined;
      (sse.addEventListener as any)(sub.event, invalidate, json);
    }
    const onConnected = (data?: { connection_id?: string }) => {
      if (!canSkipSseConnect(queryClient, sse, queryKeyRef.current.value, data?.connection_id)) invalidate();
    };
    (sse.addEventListener as any)('base:connected', onConnected);

    return () => {
      for (const sub of subs) {
        const json: SSEMarketBody | undefined = sub.topics ? { topics: sub.topics } : undefined;
        (sse.removeEventListener as any)(sub.event, invalidate, json);
      }
      (sse.removeEventListener as any)('base:connected', onConnected);
    };
  }, [rest.enabled, sse, invalidate, subsStr]);

  return query;
};
